import { FACEBOOK_COMPETITOR_SEARCH, resolve } from '@/api/endPoints';
import { FACEBOOK, INSTAGRAM } from '@/config/constants';
import { useLocales } from '@/locales';
import { LomavisPlatformIcon } from '@/lomavisSharedUi/atoms/icons/LomavisPlatformIcon';
import { LomavisDateRangeInput1 } from '@/lomavisSharedUi/atoms/inputs/LomavisRangeCalendar1';
import { LomavisCompetitorSelect } from '@/lomavisSharedUi/molecules/segments/LomavisCompetitorSelect';
import axiosClient from '@/utils/axios';
import { useTranslate } from '@tolgee/react';
import React, { useState } from 'react';
import { Button, ButtonGroup, Modal, Spinner } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { generatePath } from 'react-router-dom';

interface Props {
  platforms: string[];
  selectedPlatform: string;
  setSelectedPlatform: React.Dispatch<React.SetStateAction<number>>;
  platformInformation: any;
  allCompetitors: any;
  setDate: (date) => void;
  removeCompetitor: (uuid: string) => void;
  addCompetitor: (competitorData: {
    platform_id: string;
    username: string;
  }) => void;
  submittingCompetitor: boolean;
  removingCompetitorUuid: string | null;
  loadingCompetitors: boolean;
  totalCompetitors: number | null;
  maxCompetitors: number | null;
  instagram_direct_connect: boolean;
}
const CommonHeader: React.FC<Props> = ({
  platforms,
  selectedPlatform,
  setSelectedPlatform,
  platformInformation,
  allCompetitors,
  setDate,
  removeCompetitor,
  addCompetitor,
  submittingCompetitor,
  removingCompetitorUuid,
  loadingCompetitors,
  totalCompetitors,
  maxCompetitors,
  instagram_direct_connect
}) => {
  const { t } = useTranslate();
  const { currentLang } = useLocales();

  const [showConfirmRemove, setShowConfirmRemove] = useState(false);
  const [competitorToRemove, setCompetitorToRemove] = useState<string | null>(
    null
  );

  const handleRequestRemove = (competitor: string) => {
    setCompetitorToRemove(competitor);
    setShowConfirmRemove(true);
  };

  const handleConfirmRemove = () => {
    if (competitorToRemove) {
      removeCompetitor(competitorToRemove); // your existing remove function
    }
    setShowConfirmRemove(false);
    setCompetitorToRemove(null);
  };
  /*'https://lomavis-staging.herokuapp.com/api/v1/platforms/facebook/account/facebook/30033cd5-29c1-490d-8edf-9e8f839c37eb/search/accounts'*/
  return (
    <div className="w-100 h-100">
      {!loadingCompetitors ? (
        <div className="w-100 h-100 d-flex flex-column gap-1 mb-4 pe-3">
          <ButtonGroup className="d-flex gap-3 flex-wrap">
            {platforms?.map((platform, index) => (
              <div key={platform}>
                <Button
                  key={platform}
                  variant={'white'}
                  className={`border d-flex align-items-center gap-2 px-3 py-2 ${
                    selectedPlatform === platform &&
                    'border border-primary border-2'
                  }`}
                  onClick={() => setSelectedPlatform(index)}
                >
                  <LomavisPlatformIcon platform={platform} />
                  <span>{t(platform)}</span>
                </Button>
              </div>
            ))}
          </ButtonGroup>

          <div className="w-100 d-flex justify-content-between align-items-center gap-2">
            <div className="flex-grow-1 ">
              <div
                className="cursor-pointer d-flex flex-nowrap"
                onClickCapture={(event) => {
                  if (
                    instagram_direct_connect &&
                    selectedPlatform === INSTAGRAM
                  ) {
                    event.preventDefault();
                    event.stopPropagation();
                    toast.error(
                      ` ${t('competitorAnalysis.IG_direct_connect.error')}`,
                      {
                        duration: 4000,
                        position: 'top-right'
                      }
                    );
                  }
                }}
              >
                <LomavisCompetitorSelect
                  competitors={allCompetitors}
                  addCompetitor={(value) => addCompetitor(value)}
                  removeCompetitor={(uuid) => handleRequestRemove(uuid)}
                  currentPlatform={selectedPlatform}
                  fbSearchEndpoint={
                    platformInformation?.[FACEBOOK]?.[0]?.uuid &&
                    generatePath(`/api/v1${FACEBOOK_COMPETITOR_SEARCH}`, {
                      uuid:
                        platformInformation?.[FACEBOOK]?.[0]?.uuid ??
                        platformInformation?.[INSTAGRAM]?.[0]?.uuid
                    })
                  }
                  addCompetitorEndpoint={resolve('competitor_analysis', {}, {})}
                  submittingCompetitor={submittingCompetitor}
                  removingCompetitorUuid={removingCompetitorUuid}
                  totalCompetitors={totalCompetitors}
                  maxCompetitors={maxCompetitors}
                />
              </div>
            </div>
            <div className="flex-shrink-0">
              <LomavisDateRangeInput1
                currentLang={currentLang}
                defaultIndex={2}
                demo={false}
                setDates={(value) => {
                  setDate(value);
                }}
              />
            </div>
          </div>

          <Modal
            show={showConfirmRemove}
            onHide={() => setShowConfirmRemove(false)}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>
                {t('competitorAnalysis.removeModal.title')}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>{t('competitorAnalysis.removeModal.body')}</Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowConfirmRemove(false)}
              >
                {t('cancelModal')}
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirmRemove}
                disabled={removingCompetitorUuid === competitorToRemove} // your spinner state
              >
                {removingCompetitorUuid === competitorToRemove ? (
                  <Spinner as="span" animation="border" size="sm" />
                ) : (
                  t('remove')
                )}
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      ) : (
        <div className="w-100 h-100 mb-4 pe-3">
          {' '}
          <div className="skeleton-loader h-150px rounded w-100"></div>
        </div>
      )}
    </div>
  );
};

export default CommonHeader;
